import { useAverageAssignmentDurationsGetAverageAssignmentDurationHook } from 'api/average-assignment-durations/average-assignment-durations';
import { AssignmentTypeEnum, CaseTypeEnum } from 'api/model';
import Select from 'components/inputs/Select';
import TextButton from 'components/inputs/TextButton';
import useTranslations from 'contexts/basicData/useTranslations';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { IAddress } from 'utils/address';
import { openGoogleMapsDirectionsArray } from 'utils/address-helper';
import { getHoursAndMinutesFromMillisecondsString } from 'utils/date-helpers';
import esitimatedAssignmentDurations from 'utils/esitimatedAssignmentDurations';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const HighlightedText = styled.span`
  color: ${({ theme }) => theme.colors.foreground.tint};
`;

interface Props {
  caseType: CaseTypeEnum;
  assignmentType: AssignmentTypeEnum;
  disabled?: boolean;
  error?: boolean;
  onChange: (estimatedDuration: number) => void;
  width?: number;
  value?: number;
  hideMapsIcon?: boolean;
  addresses: IAddress[];
  placeholder?: string;
  max?: number;
}
const BasicEstimatedDurationPicker: React.FC<Props> = ({
  assignmentType,
  caseType,
  disabled,
  width,
  hideMapsIcon,
  addresses,
  error,
  value,
  placeholder,
  max,

  onChange,
}) => {
  const selectRef = useRef<HTMLSelectElement>(null);
  const [suggestedEstimatedDuration, setSuggestedEstimatedDuration] = useState<
    { min: number; max: number; average: number } | undefined
  >();
  const { zipCodeAreas } = useTranslations();
  const avgDurationCallTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const captureFn = (event: Event) => {
      event.preventDefault();
    };
    selectRef.current?.addEventListener('blur', captureFn);
  }, []);

  const getAverageAssignmentDurationCall =
    useAverageAssignmentDurationsGetAverageAssignmentDurationHook();

  useEffect(
    () => {
      const fetchAverageDuration = async () => {
        // if (state)
        const fromAddr = addresses[0];
        const toAddr = addresses[1];
        if (
          !fromAddr?.county?.areaName ||
          !toAddr?.county?.areaName ||
          !fromAddr?.zip ||
          !toAddr?.zip
        ) {
          return; // we need them for the average duration
        }

        // get the number of minutes from the average duration
        const result = await getAverageAssignmentDurationCall({
          caseType,
          assignmentType,
          fromZipCode: fromAddr?.zip,
          toZipCode: toAddr?.zip,
          fromCounty: fromAddr?.county.areaName,
          toCounty: toAddr?.county.areaName,
        });

        if (result !== null && result.sampleSize > 0) {
          // convert the number of seconds to milliseconds in order to use the estimatedDurationOptions array
          const millisAverage = Math.round(result.average * 1000 * 60);
          const millisMin = result.min * 1000 * 60;
          const millisMax = result.max * 1000 * 60;

          // return the nearest value in the estimatedDurationOptions array
          const nearestAverage =
            esitimatedAssignmentDurations.getNearestEstimatedDuration(
              millisAverage
            );
          const nearestMin =
            esitimatedAssignmentDurations.getNearestEstimatedDuration(
              millisMin
            );
          const nearestMax =
            esitimatedAssignmentDurations.getNearestEstimatedDuration(
              millisMax
            );

          setSuggestedEstimatedDuration({
            min: nearestMin,
            max: nearestMax,
            average: nearestAverage,
          });
        } else {
          setSuggestedEstimatedDuration(undefined);
        }
      };
      if (avgDurationCallTimeoutRef.current) {
        clearTimeout(avgDurationCallTimeoutRef.current);
      }
      avgDurationCallTimeoutRef.current = setTimeout(fetchAverageDuration, 500);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [caseType, zipCodeAreas]
  );

  const estimatedDurationOptions = useMemo(() => {
    const result = esitimatedAssignmentDurations.estimatedDurationOptions;
    if (
      suggestedEstimatedDuration !== undefined &&
      !result.includes(suggestedEstimatedDuration.average)
    ) {
      result.push(suggestedEstimatedDuration.average);
    }

    if (value && !result.includes(value)) {
      result.push(value);
    }

    // sort the array
    result.sort((a, b) => a - b);

    return result;
  }, [suggestedEstimatedDuration, value]);

  return (
    <Wrapper>
      <Select
        disabled={disabled}
        error={error}
        onChange={(eve) => {
          onChange(Number(eve.currentTarget.value));
          eve.currentTarget.blur();
        }}
        ref={selectRef}
        small
        width={width}
        value={value}
      >
        <option key="unset" value={undefined}>
          {placeholder ?? 'Välj estimerad tid'}
        </option>
        {estimatedDurationOptions.map((option) => (
          <option
            key={option}
            value={option}
            disabled={max ? option > max : false}
          >
            {getHoursAndMinutesFromMillisecondsString(option)}
            {suggestedEstimatedDuration?.average === option && ' (genomsnitt)'}
          </option>
        ))}
      </Select>
      <HighlightedText>
        {!hideMapsIcon && (
          <TextButton
            onClick={() => {
              openGoogleMapsDirectionsArray(addresses);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Öppna i Google Maps"
          >
            <img
              alt="Google maps icon"
              height={15}
              src="/images/googlemapsiconsmall.png"
              width={15}
            />
          </TextButton>
        )}
      </HighlightedText>
    </Wrapper>
  );
};
export default BasicEstimatedDurationPicker;
